import { useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useIdle } from "../../providers/IdleProvider";

// Keep the backend session alive while the user is detectably at the device,
// without requiring them to click anything. The backend's session_enforcer
// only bumps `user_token_used_at` on actual HTTP requests, so a user
// reading a dashboard for 5+ minutes between clicks would otherwise get
// kicked on their next click. This ping every 60s solves that — as long as
// IdleProvider considers them active.
//
// When IdleProvider declares them idle (no input for `idleSeconds` per the
// tenant config, which feeds idleAfterMs in src/index.js), heartbeats stop
// and the backend's normal idle-timeout takes over.
//
// Heartbeats are silent on failure — if the session is already gone (401),
// Guard.js will handle the redirect on the next real interaction.

const HEARTBEAT_INTERVAL_MS = 60 * 1000;

export default function SessionHeartbeat() {
    const loggedIn = useSelector((s) => s?.loggedIn?.value);
    const { idle } = useIdle();

    useEffect(() => {
        if (!loggedIn || idle) return;

        const base = process.env.REACT_APP_BASE_SERVER;
        if (!base) return;

        const send = () => {
            axios({
                method: "POST",
                url: `${base}/account/heartbeat`,
                withCredentials: true,
            }).catch(() => {
                // Silent: if the session is gone, the next real interaction
                // gets a 401 and Guard redirects to login. No need to spam.
            });
        };

        const id = setInterval(send, HEARTBEAT_INTERVAL_MS);
        return () => clearInterval(id);
    }, [loggedIn, idle]);

    return null;
}
